import { VISIBLE_MATTER_NAME_LIMIT } from '@/constants/settlementConstants';
import type {
  MatterNameSummary,
  PaymentInvoiceReference,
} from '@/models/settlementTypes';

/**
 * Matters a Step 5 payment spans — the union across its invoices. Null below two
 * matters: nothing to tag. Visibility only; the payment amount is never read here.
 */
export function summarizePaymentMatters(
  invoices: PaymentInvoiceReference[] | null | undefined,
): MatterNameSummary | null {
  // The backend emits one entry per matter id but sends us only names, so a name repeated
  // within one invoice is two distinct same-named matters and both must survive. Across
  // invoices the same matter recurs, so a name contributes its highest single-invoice
  // count rather than the sum.
  const highest = new Map<string, number>();

  (invoices ?? []).forEach((invoice) => {
    const perInvoice = new Map<string, number>();
    (invoice?.matterNames ?? []).forEach((rawName) => {
      const name = rawName?.trim();
      if (!name) return;
      perInvoice.set(name, (perInvoice.get(name) ?? 0) + 1);
    });
    perInvoice.forEach((count, name) => {
      highest.set(name, Math.max(highest.get(name) ?? 0, count));
    });
  });

  const all: string[] = [];
  highest.forEach((count, name) => {
    for (let i = 0; i < count; i += 1) all.push(name);
  });

  // Locale pinned so the order is the same for every user and every CI runner — an
  // unpinned localeCompare files "Ångstrom" after "Zuniga" under sv/da.
  all.sort((a, b) => a.localeCompare(b, 'en'));

  if (all.length < 2) return null;

  return {
    all,
    visible: all.slice(0, VISIBLE_MATTER_NAME_LIMIT),
    hiddenCount: Math.max(0, all.length - VISIBLE_MATTER_NAME_LIMIT),
  };
}
